/* eslint-disable */
import React, { useState, useRef } from 'react';
import ReactDOM from 'react-dom';

/**
 * DatePicker — shared date picker component.
 * Extracted from App.js to be reusable across features.
 *
 * Props:
 *   value — ISO date string (YYYY-MM-DD) or empty
 *   onChange — receives { target: { value } } (mimics native input for compat)
 *   placeholder, style, darkMode, OW, DM — existing hooks kept for App.js callers
 */
export default function DatePicker({value,onChange,placeholder="dd/mm/yyyy",style={},darkMode,OW,DM={}}){
  const[open,setOpen]=useState(false);
  const triggerRef=useRef(null);
  const[dropPos,setDropPos]=useState({top:0,left:0});
  const today=new Date();
  const parsed=value?new Date(value+(!value.includes("T")?"T00:00:00":"")):null;
  const[viewYear,setViewYear]=useState(parsed?.getFullYear()||today.getFullYear());
  const[viewMonth,setViewMonth]=useState(parsed?.getMonth()??today.getMonth());
  const MONTHS=["January","February","March","April","May","June","July","August","September","October","November","December"];
  const DAYS=["Mo","Tu","We","Th","Fr","Sa","Su"];
  const firstDay=new Date(viewYear,viewMonth,1);
  const lastDay=new Date(viewYear,viewMonth+1,0);
  const startDow=(firstDay.getDay()+6)%7;
  const cells=[];
  for(let i=0;i<startDow;i++)cells.push(null);
  for(let d=1;d<=lastDay.getDate();d++)cells.push(d);
  const displayValue=parsed?`${String(parsed.getDate()).padStart(2,"0")}/${String(parsed.getMonth()+1).padStart(2,"0")}/${parsed.getFullYear()}`:"";
  const prevMonth=()=>{if(viewMonth===0){setViewMonth(11);setViewYear(y=>y-1);}else setViewMonth(m=>m-1);};
  const nextMonth=()=>{if(viewMonth===11){setViewMonth(0);setViewYear(y=>y+1);}else setViewMonth(m=>m+1);};
  const navBtn={background:"none",border:"none",cursor:"pointer",color:"var(--text-default)",fontSize:16,fontWeight:700,padding:"2px 8px",borderRadius:6,lineHeight:1};
  const footBtn={background:"none",border:"none",cursor:"pointer",fontSize:11,fontWeight:600,padding:"4px 10px",borderRadius:6,color:"var(--brand-default)"};
  const textCol = DM.text || "var(--text-default)";
  const subCol = DM.sub || "var(--text-subtle)";
  const PANEL_W=252,GAP=5,EDGE=8;
  // Fallback only, for the very first paint before the panel exists to be measured.
  const PANEL_H_GUESS=330;
  const panelRef=useRef(null);

  // The panel always opens UPWARD, sitting on the field's top edge.
  //
  // It used to open downward and flip only when it "would not fit", but deciding that needs
  // a trustworthy viewport height, and window.innerHeight reports more than is actually
  // visible (browser chrome, OS taskbar) — so the panel kept being placed below and running
  // off the bottom of the screen. Anchoring to the field's TOP removes the viewport from the
  // calculation altogether: the space above a field is what it is, whatever the window
  // claims. Date fields in this app sit low in their forms, so upward is the right default
  // regardless.
  const placePanel=()=>{
    const r=triggerRef.current?.getBoundingClientRect();
    if(!r)return;
    const h=panelRef.current?.offsetHeight||PANEL_H_GUESS;
    const w=panelRef.current?.offsetWidth||PANEL_W;
    // Never let it run off the top either — a field near the very top of the page pins the
    // panel to the top edge instead, where maxHeight lets it scroll if it has to.
    const top=Math.max(EDGE,r.top-GAP-h);
    const left=Math.max(EDGE,Math.min(r.left,window.innerWidth-w-EDGE));
    // Cap to the room above the field, measured from the field itself rather than from the
    // viewport, so a panel taller than that space scrolls instead of covering the input.
    setDropPos({top,left,maxH:Math.max(160,r.top-GAP-EDGE)});
  };
  // Measure once the panel has actually rendered, then place it. Without this pass the
  // first open would still be positioned from the fallback height.
  React.useLayoutEffect(()=>{
    if(!open)return;
    placePanel();
  },[open,viewMonth,viewYear]);
  // Recompute while open: scrolling or resizing would otherwise leave the panel behind,
  // since it is fixed-positioned and detached from the field.
  React.useEffect(()=>{
    if(!open)return;
    const h=()=>placePanel();
    window.addEventListener("scroll",h,true);
    window.addEventListener("resize",h);
    return()=>{window.removeEventListener("scroll",h,true);window.removeEventListener("resize",h);};
  },[open]);
  return(
    <div style={{position:"relative",...style}}>
      <div ref={triggerRef} onClick={()=>{if(!open)placePanel();setOpen(v=>!v);}} style={{display:"flex",alignItems:"center",gap:8,height:"var(--height-input-md)",padding:"0 var(--space-3)",borderRadius:"var(--radius-lg)",cursor:"pointer",border:"1px solid "+(open?"var(--brand-border)":"var(--border-default)"),boxShadow:open?"0 0 0 3px var(--brand-tint)":"none",background:"var(--surface-inset)",userSelect:"none",transition:"border-color 0.15s,box-shadow 0.15s"}}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={displayValue?"var(--brand-default)":"var(--text-subtle)"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
        <span style={{flex:1,fontSize:13,color:displayValue?textCol:subCol,fontWeight:displayValue?500:400}}>{displayValue||placeholder}</span>
        {value&&<span onMouseDown={e=>{e.stopPropagation();onChange({target:{value:""}});}} style={{color:"var(--text-muted)",fontSize:15,lineHeight:1,padding:"0 2px",cursor:"pointer"}}>×</span>}
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={subCol} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{transition:"transform 0.15s",transform:open?"rotate(180deg)":"none",flexShrink:0}}><polyline points="6 9 12 15 18 9"/></svg>
      </div>
      {open&&ReactDOM.createPortal(<>
        <div onMouseDown={()=>setOpen(false)} style={{position:"fixed",inset:0,zIndex:998}}/>
        <div ref={panelRef} style={{position:"fixed",top:dropPos.top,left:dropPos.left,zIndex:999,background:"var(--surface-default)",border:"1px solid var(--border-default)",borderRadius:12,boxShadow:"0 8px 32px rgba(0,0,0,0.16)",padding:14,minWidth:252,
          // Guard for a field close to the top of the page: scroll inside the space above
          // rather than overlapping the input.
          maxHeight:dropPos.maxH||"calc(100vh - 16px)",overflowY:"auto"}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
            <button style={navBtn} onClick={prevMonth}>‹</button>
            <span style={{fontWeight:700,fontSize:13,color:"var(--text-default)"}}>{MONTHS[viewMonth]} {viewYear}</span>
            <button style={navBtn} onClick={nextMonth}>›</button>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2,marginBottom:6}}>
            {DAYS.map(d=><div key={d} style={{textAlign:"center",fontSize:10,color:"var(--text-muted)",fontWeight:600,padding:"2px 0"}}>{d}</div>)}
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2}}>
            {cells.map((day,i)=>{
              if(!day)return<div key={i}/>;
              const iso=`${viewYear}-${String(viewMonth+1).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
              const isSel=value===iso;
              const isToday=day===today.getDate()&&viewMonth===today.getMonth()&&viewYear===today.getFullYear();
              return(<button key={i} onClick={()=>{onChange({target:{value:iso}});setOpen(false);}} style={{textAlign:"center",padding:"6px 2px",borderRadius:6,border:"none",cursor:"pointer",fontSize:12,background:isSel?"var(--brand-default)":isToday?"var(--brand-tint)":"transparent",color:isSel?"#fff":isToday?"var(--brand-light)":"var(--text-default)",fontWeight:isSel||isToday?700:400,outline:isToday&&!isSel?"1.5px solid var(--brand-border)":"none",outlineOffset:"-1px"}}>{day}</button>);
            })}
          </div>
          <div style={{display:"flex",justifyContent:"space-between",marginTop:10,paddingTop:8,borderTop:"1px solid var(--border-default)"}}>
            <button style={footBtn} onClick={()=>{onChange({target:{value:""}});setOpen(false);}}>Clear</button>
            <button style={{...footBtn,background:"var(--brand-tint)"}} onClick={()=>{const t=new Date();setViewYear(t.getFullYear());setViewMonth(t.getMonth());onChange({target:{value:t.toISOString().slice(0,10)}});setOpen(false);}}>Today</button>
          </div>
        </div>
      </>,document.body)}
    </div>
  );
}
